import styled from '@emotion/styled';
import { useEffect } from 'react';
import Button from '../../common/components/form/Button';
import Icon from '../../common/components/Icon';
import { Col, Row } from '../../common/components/layout/Grid';
import { ExternalLink } from '../../common/components/Link';
import { BORDER_RADIUS, BORDER_WIDTH } from '../../common/styles/borders';
import { MEDIA_QUERY } from '../../common/styles/breakpoints';
import { COLOR, GREYSCALE } from '../../common/styles/colors';
import { SPACING } from '../../common/styles/spacing';
import { TYPOGRAPHY } from '../../common/styles/typography';
import Z_INDEX from '../../common/styles/zindex';
import triggerGoogleTagManagerCustomEvent from '../../common/utils/triggerGoogleTagManagerCustomEvent';
import cookieIconUrl from '../assets/cookie.png';

const Styled = {
  CookieBanner: styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
    background: ${GREYSCALE.white};
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    z-index: ${Z_INDEX.fixed};

    @media (min-width: ${MEDIA_QUERY.mdMin}) {
      left: auto;
      right: ${SPACING.xl};
      bottom: ${SPACING.xl};
      border-radius: ${BORDER_RADIUS.sm};
      width: 400px;
    }
  `,
  CookieBannerContent: styled('div')`
    position: relative;
    padding: ${SPACING.xl};

    @media (max-width: ${MEDIA_QUERY.mdMin}) {
      padding-top: 12px;
      padding-bottom: 12px;
    }
  `,
  Heading: styled('p')`
    font-size: ${TYPOGRAPHY.heading.md.fontSize};
    line-height: ${TYPOGRAPHY.heading.md.lineHeight};
    font-weight: ${TYPOGRAPHY.heading.xxl.fontWeight};
    margin: 0 0 ${SPACING.xs} !important; // important to override legacy app styles
  `,
  Paragraph: styled('p')`
    margin: 0 0 ${SPACING.lg} !important; // important to override legacy app styles
  `,
  LabelWithIconPrefix: styled.span`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  `,
  CookieIconPrefix: styled.img`
    width: 20px;
    height: 24px;
    margin: -2px ${SPACING.sm} 0 0;
  `,
  CloseIcon: styled.button`
    position: absolute;
    top: ${SPACING.md};
    right: ${SPACING.xs};
    width: ${SPACING.xxl};
    height: ${SPACING.xxl};
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    background: none;
    border: none;
    :focus-visible {
      outline: ${BORDER_WIDTH.sm} auto ${COLOR.blue};
      outline-offset: -4px;
    }

    color: ${GREYSCALE.black};
    cursor: pointer;

    :hover {
      color: ${GREYSCALE.grey60};
    }

    @media (max-width: ${MEDIA_QUERY.mdMin}) {
      top: ${SPACING.xxs};
    }
  `,
};

export type CookieBannerProps = {
  onAccept?: () => void;
  onReject?: () => void;
};

export default function CookieBanner({ onAccept, onReject }: CookieBannerProps) {
  useEffect(() => {
    triggerGoogleTagManagerCustomEvent('cookiebanner_shown');
  }, []);

  return (
    <Styled.CookieBanner>
      <Styled.CookieBannerContent>
        <Styled.CloseIcon id="close" onClick={onAccept} aria-label="Close Cookie Banner">
          <Icon icon="times" size="lg" />
        </Styled.CloseIcon>

        <Styled.Heading>What&apos;s a pirate&apos;s favorite cookie?</Styled.Heading>
        <Styled.Paragraph>
          Chips Ahoy! Our site uses cookies and similar technologies unless you reject.{' '}
          <ExternalLink href="https://www.pirateship.com/dataprivacy">Learn more</ExternalLink>
        </Styled.Paragraph>

        <Row>
          <Col xs={6}>
            <Button fullWidth variant="primaryOutline" onClick={onReject}>
              Reject Cookies
            </Button>
          </Col>
          <Col xs={6}>
            <Button fullWidth variant="primaryOutline" onClick={onAccept}>
              <Styled.LabelWithIconPrefix>
                <Styled.CookieIconPrefix src={cookieIconUrl} alt="Pirate's favorite cookie" />
                Close
              </Styled.LabelWithIconPrefix>
            </Button>
          </Col>
        </Row>
      </Styled.CookieBannerContent>
    </Styled.CookieBanner>
  );
}
